import { ArticleTile, Container, Heading, HorizontalRule, LinkButton, Page, Section } from '@troon/ui';
import { Meta, Title } from '@solidjs/meta';
import { createAsync } from '@solidjs/router';
import { cachedGet } from '@troon/api-client';
import { For, Match, Show, Suspense, Switch } from 'solid-js';
import dayjs from '@troon/dayjs';
import { IconChevronRight } from '@troon/icons/chevron-right';
import { RichText } from '@troon/contentful/rich-text';
import { NoHydration } from 'solid-js/web';
import { TeeTimeSearch } from '../../components/tee-time-search';
import { getConfigValue } from '../../modules/config';
import { useUser } from '../../providers/user';
import { FacilityCard } from '../../components/facility/card';
import { EventArticle } from '../../components/event-article';
import { PopularDestination } from '../../components/popular-destination';
import { TransferPointsBanner } from '../../components/points-transfer/transfer-points-banner';
import { TopoBg } from '../../components/topo-bg';
import { AppDownloadUpsell } from '../../components/upsells/app-download';
import { UpsellAccessMinimal } from '../../components/upsells/access-minimal';
import { UpsellJoinMinimal } from '../../components/upsells/join-minimal';
import { createContentfulListRequest } from '../(api)/content/[model]/_client';
import { Advertisement } from '../../components/advertisement/advertisement';
import type { RouteDefinition } from '@solidjs/router';

export default function Home() {
	const user = useUser();
	const facilities = createAsync(
		async () => {
			const [near, featured] = await Promise.all([
				getNearbyFacilities({ query: { radiusInMiles: 100, isBookable: true } }),
				getFacilities({ query: { featuredOnGolfWithAccess: true, limit: 3, isBookable: true } }),
			]);
			return [...near.facilities.map(({ facility }) => facility), ...featured.facilities].slice(0, 3);
		},
		{
			deferStream: true,
		},
	);
	const events = createAsync(async () => getEvents({ 'fields.endDate[gt]': dayjs().format('YYYY-MM-DD') }), {
		deferStream: false,
	});
	const destinations = createAsync(() => getDestinations({}), { deferStream: false });
	const articles = createAsync(async () => getArticles(), { deferStream: false });

	return (
		<>
			<Title>Golf with Access | Book tee times and earn points at your favorite golf courses</Title>
			<Meta
				name="description"
				content="Book tee times, earn points, and unlock exclusive rates at top courses with Access. Play more, save more, and get rewarded for every round."
			/>
			<Meta name="og:image" content={`${getConfigValue('IMAGE_HOST')}/og/access.jpg`} />

			<NoHydration>
				<TopoBg placement="top" class="absolute z-0 h-48 w-screen" />
			</NoHydration>

			<Container>
				<Page>
					<Page class="md:gap-16">
						<Section>
							<div class="flex flex-col gap-4 pt-6">
								<p>
									Welcome{' '}
									<Suspense fallback="to Access">
										<Show when={user()} fallback="to Access">
											{(user) => user().firstName}
										</Show>{' '}
										~
									</Suspense>
								</p>
								<Heading as="h1" class="text-3xl lg:text-6xl">
									Where do you want to play?
								</Heading>
							</div>
							<TeeTimeSearch onSubmit={() => {}} withFilters />
						</Section>

						<Advertisement
							index={1}
							id={user()?.troonAccessProductType !== 'TROON_ACCESS_ELITE' ? 'elite' : undefined}
						/>

						<Section>
							<Heading as="h2" size="h4">
								Featured Courses
							</Heading>
							<ul class="grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-3">
								<Suspense>
									<For each={facilities()}>
										{(facility) => (
											<li>
												<FacilityCard facility={facility} level="h3" loading="eager" />
											</li>
										)}
									</For>
								</Suspense>
							</ul>
						</Section>

						<Switch>
							<Match when={!user()}>
								<Advertisement index={2} id="access-join">
									<UpsellJoinMinimal location="home" />
								</Advertisement>
							</Match>
							<Match when={!user()?.accessRewardsOptIn}>
								<Advertisement index={2} id="access-transfer-points">
									<TransferPointsBanner location="home" />
								</Advertisement>
							</Match>
							<Match when={!user()?.troonAccessProductType}>
								<Advertisement index={2} id="premium-join">
									<UpsellAccessMinimal location="home" />
								</Advertisement>
							</Match>
						</Switch>

						<Section>
							<div class="flex flex-row flex-wrap items-center justify-between">
								<Heading as="h2" size="h4">
									What’s New
								</Heading>
								<LinkButton href="/insider">
									<span class="sr-only md:not-sr-only">
										View all <span class="sr-only">Articles</span>
									</span>
									<IconChevronRight />
								</LinkButton>
							</div>
							<div class="grid grid-cols-12 gap-8">
								<For each={articles()?.items.slice(0, 3)}>
									{(article, i) => (
										<div class={i() === 0 ? 'col-span-12 row-span-2 md:col-span-8' : 'col-span-6 md:col-span-4'}>
											<ArticleTile
												appearance="vertical-only"
												title={article.fields.title}
												url={`/insider/${article.fields.slug}`}
												date={dayjs(article.fields.publishDate, 'America/Phoenix').toDate()}
												hero={
													article.fields.heroImage
														? {
																src: article.fields.heroImage?.fields.file?.url,
																alt:
																	article.fields.heroImage?.fields.description ??
																	article.fields.heroImage?.fields.title ??
																	'',
																preload: true,
																loading: 'eager',
															}
														: undefined
												}
											>
												<RichText document={article.fields.summary} />
											</ArticleTile>
										</div>
									)}
								</For>
							</div>
						</Section>
					</Page>

					<AppDownloadUpsell />

					<Section>
						<div class="flex flex-row flex-wrap items-center justify-between">
							<Heading as="h2" size="h4">
								Upcoming Events
							</Heading>
							<LinkButton href="/events">
								<span class="sr-only md:not-sr-only">
									View all <span class="sr-only">Events</span>
								</span>
								<IconChevronRight />
							</LinkButton>
						</div>

						<Suspense>
							<For each={events()?.items}>
								{(event) => (
									<div class="rounded-lg border border-neutral p-4 md:p-6">
										<EventArticle event={event} preload={false} />
									</div>
								)}
							</For>
						</Suspense>
					</Section>

					<HorizontalRule />

					<Section>
						<Heading as="h2" size="h4">
							Popular Destinations
						</Heading>

						<ul class="grid grid-cols-2 gap-4 lg:grid-cols-3 xl:grid-cols-4">
							<Suspense>
								<Show when={destinations()}>
									{(dests) => (
										<For each={[...dests().places, ...dests().regions]}>
											{(dest) => (
												<li>
													<PopularDestination destination={dest} />
												</li>
											)}
										</For>
									)}
								</Show>
							</Suspense>
						</ul>
					</Section>
				</Page>
			</Container>
		</>
	);
}

const getNearbyFacilities = cachedGet('/v0/facilities/nearby');
const getFacilities = cachedGet('/v0/facilities');
const getArticles = createContentfulListRequest('articles', {
	'fields.articleType': 'The Troon Insider',
	limit: 3,
});
const getEvents = createContentfulListRequest('events', { limit: 3 });
const getDestinations = cachedGet('/v0/search/popular-destinations');

export const route = {
	info: { nav: {} },
	preload: async () =>
		Promise.all([
			getNearbyFacilities({ query: { radiusInMiles: 100 } }),
			getFacilities({ query: { featuredOnGolfWithAccess: true, limit: 3 } }),
		]),
} satisfies RouteDefinition;
